import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { ChevronDown, Building2 } from 'lucide-react';
import { api } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import OrganizationProfileModal from './OrganizationProfileModal';

interface Organization {
  organizationId: string;
  companyName: string;
  logoUrl: string | null;
  isActive: boolean;
  dateAdded: string;
}

interface OrganizationSwitcherProps {
  effectiveOrgId: string;
}

export default function OrganizationSwitcher({ effectiveOrgId }: OrganizationSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch user's organizations
  const { data: organizations = [], isLoading } = useQuery({
    queryKey: ['user-organizations'],
    queryFn: () => api.get('/users/organizations').then(res => res.data.data),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Organization switching mutation
  const switchOrganizationMutation = useMutation({
    mutationFn: (organizationId: string) => 
      api.put('/users/active-organization', { organizationId }),
    onSuccess: (data, organizationId) => {
      console.log('✅ Organization switch successful:', organizationId);
      
      // Invalidate all queries to refresh data with new organization context
      queryClient.invalidateQueries();
      
      // Clear React Query cache completely to ensure fresh data
      queryClient.clear();
      
      // Close dropdown first
      setIsOpen(false);
      
      // Add a small delay before reload to ensure backend cache clear completes
      // This ensures the reload fetches fresh data with the new organization
      setTimeout(() => {
        // Force complete page reload to ensure all frontend state is cleared
        // This prevents cached data from the old organization from being used
        window.location.href = '/';
      }, 100);
    },
    onError: (error) => {
      console.error('❌ Failed to switch organization:', error);
      setIsOpen(false);
    }
  });

  const handleOrganizationClick = (organization: Organization) => {
    if (organization.isActive) {
      // Already active, just close dropdown
      setIsOpen(false);
      return;
    }

    // Switch to the selected organization
    switchOrganizationMutation.mutate(organization.organizationId);
  };

  const currentOrganization = organizations.find(org => org.isActive);

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2">
        <Building2 className="h-5 w-5 text-gray-400" />
        <span className="text-sm text-gray-500">Loading...</span>
      </div>
    );
  }

  if (organizations.length <= 1) {
    // Single organization - both logo and name open profile modal
    return (
      <>
        <div 
          className="flex items-center space-x-2 px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors rounded-md"
          onClick={() => setIsProfileModalOpen(true)}
        >
          {currentOrganization?.logoUrl ? (
            <img 
              src={currentOrganization.logoUrl} 
              alt={currentOrganization.companyName}
              className="h-5 w-5 rounded object-cover"
            />
          ) : (
            <Building2 className="h-5 w-5 text-gray-600" />
          )}
          <span className="text-sm font-medium text-gray-900">
            {currentOrganization?.companyName || 'Organization'}
          </span>
        </div>
        
        <OrganizationProfileModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          organizationId={effectiveOrgId}
        />
      </>
    );
  }

  return (
    <div className="relative">
      {/* Organization Switcher Button */}
      <div className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors">
        {/* Logo - opens profile modal */}
        <div 
          className="cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => setIsProfileModalOpen(true)}
        >
          {currentOrganization?.logoUrl ? (
            <img 
              src={currentOrganization.logoUrl} 
              alt={currentOrganization.companyName}
              className="h-5 w-5 rounded object-cover"
            />
          ) : (
            <Building2 className="h-5 w-5 text-gray-600" />
          )}
        </div>
        
        {/* Name and dropdown - opens organization switcher */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-1 hover:opacity-80 transition-opacity"
          disabled={switchOrganizationMutation.isLoading}
        >
          <span className="text-sm font-medium text-gray-900">
            {currentOrganization?.companyName || 'Organization'}
          </span>
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Content */}
          <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-20">
            <div className="py-1">
              {organizations.map((organization) => (
                <button
                  key={organization.organizationId}
                  onClick={() => handleOrganizationClick(organization)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                    organization.isActive ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                  }`}
                  disabled={switchOrganizationMutation.isLoading}
                >
                  {organization.logoUrl ? (
                    <img 
                      src={organization.logoUrl} 
                      alt={organization.companyName}
                      className="h-6 w-6 rounded object-cover"
                    />
                  ) : (
                    <Building2 className="h-6 w-6 text-gray-500" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {organization.companyName}
                    </div>
                    {organization.isActive && (
                      <div className="text-xs text-blue-600">Current</div>
                    )}
                  </div>
                  {switchOrganizationMutation.isLoading && organization.organizationId === switchOrganizationMutation.variables && (
                    <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      
      {/* Organization Profile Modal */}
      <OrganizationProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        organizationId={effectiveOrgId}
      />
    </div>
  );
}
